import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, Banknote, Smartphone, Building2, Tag } from "lucide-react";
import api, { taka, formatApiErrorDetail } from "@/lib/api";
import { effectivePrice, deliveryCharge } from "@/lib/pricing";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { DISTRICTS } from "@/lib/bd";

const PAY_META = {
  cod: { label: "ক্যাশ অন ডেলিভারি", icon: Banknote },
  bkash: { label: "বিকাশ", icon: Smartphone },
  nagad: { label: "নগদ", icon: Smartphone },
  bank: { label: "ব্যাংক ট্রান্সফার", icon: Building2 },
};

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const nav = useNavigate();
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [clientToken] = useState(() => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()) + Math.random()));
  const [form, setForm] = useState({
    customer_name: "", mobile: "", address: "", district: "", upazila: "", note: "", payment_method: "cod",
  });

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, customer_name: user.name, mobile: user.mobile || "" }));
      api.get("/my/addresses").then((r) => setSavedAddresses(r.data)).catch(() => {});
    }
    if (items.length === 0) nav("/cart");
  }, [user]); // eslint-disable-line

  const payNote = (id) => {
    if (id === "cod") return "পণ্য হাতে পেয়ে টাকা দিন";
    if (id === "bkash") return settings.bkash_number ? `বিকাশ: ${settings.bkash_number} — অর্ডারের পর কনফার্ম হবে` : "অর্ডারের পর কনফার্ম হবে";
    if (id === "nagad") return settings.nagad_number ? `নগদ: ${settings.nagad_number} — অর্ডারের পর কনফার্ম হবে` : "অর্ডারের পর কনফার্ম হবে";
    if (id === "bank") return settings.bank_info || "ব্যাংক তথ্যের জন্য যোগাযোগ করুন";
    return "";
  };
  const activePayments = (settings.payment_methods || ["cod", "bkash", "nagad", "bank"]).filter((id) => PAY_META[id]);

  const delivery = deliveryCharge(items, settings, form.district, form.upazila);
  const total = subtotal + delivery - discount;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupon/validate", { code: coupon.trim(), subtotal });
      setDiscount(data.discount);
      toast.success(`কুপন প্রয়োগ হয়েছে (${data.percent}% ছাড়)`);
    } catch (err) {
      setDiscount(0);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.mobile || !form.address || !form.district) {
      toast.error("অনুগ্রহ করে সব প্রয়োজনীয় তথ্য দিন"); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        ...form, coupon: coupon || null, client_token: clientToken,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      nav(`/order-confirm/${data.order_no}`, { state: { order: data } });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const upazilas = DISTRICTS.find((d) => d.name === form.district)?.upazilas || [];
  const inp = "w-full h-12 px-4 rounded-xl border border-stone-300 bg-white focus:outline-none focus:ring-2 focus:ring-[#1E3A2B]/30";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <h1 className="text-2xl font-bold text-[#1E3A2B] mb-5 flex items-center gap-2"><Truck /> চেকআউট</h1>
      <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-bold text-[#1E3A2B] mb-4">ডেলিভারি তথ্য</h3>
            {savedAddresses.length > 0 && (
              <div className="mb-4">
                <div className="text-sm font-medium text-stone-600 mb-2">সংরক্ষিত ঠিকানা</div>
                <div className="flex flex-wrap gap-2">
                  {savedAddresses.map((a) => (
                    <button type="button" key={a.id} data-testid={`saved-addr-${a.id}`}
                      onClick={() => setForm((f) => ({ ...f, customer_name: a.name, mobile: a.mobile, address: a.address, district: a.district, upazila: a.upazila }))}
                      className="text-left border border-stone-200 rounded-xl px-3 py-2 hover:border-[#1E3A2B] text-sm">
                      <span className="font-semibold text-[#1E3A2B]">{a.label}</span> — {a.name}<div className="text-xs text-stone-500 truncate max-w-[200px]">{a.address}, {a.district}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input placeholder="নাম *" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className={inp} data-testid="checkout-name" />
              <input placeholder="মোবাইল নম্বর *" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className={inp} data-testid="checkout-mobile" />
              <select value={form.district} onChange={(e) => { set("district", e.target.value); set("upazila", ""); }} className={inp} data-testid="checkout-district">
                <option value="">জেলা নির্বাচন *</option>
                {DISTRICTS.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
              </select>
              <select value={form.upazila} onChange={(e) => set("upazila", e.target.value)} className={inp} data-testid="checkout-upazila">
                <option value="">উপজেলা নির্বাচন</option>
                {upazilas.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
              <textarea placeholder="সম্পূর্ণ ঠিকানা *" value={form.address} onChange={(e) => set("address", e.target.value)} className={`${inp} h-20 py-3 sm:col-span-2`} data-testid="checkout-address" />
              <textarea placeholder="অতিরিক্ত নির্দেশনা (ঐচ্ছিক)" value={form.note} onChange={(e) => set("note", e.target.value)} className={`${inp} h-16 py-3 sm:col-span-2`} data-testid="checkout-note" />
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-stone-200 p-5">
            <h3 className="font-bold text-[#1E3A2B] mb-4">পেমেন্ট পদ্ধতি</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {activePayments.map((id) => { const P = PAY_META[id]; return (
                <button type="button" key={id} onClick={() => set("payment_method", id)} data-testid={`pay-${id}`}
                  className={`text-left p-4 rounded-xl border-2 transition-all ${form.payment_method === id ? "border-[#1E3A2B] bg-emerald-50" : "border-stone-200 bg-white"}`}>
                  <div className="flex items-center gap-2 font-semibold text-[#1C1917]"><P.icon size={18} /> {P.label}</div>
                  <div className="text-xs text-stone-500 mt-1">{payNote(id)}</div>
                </button>
              ); })}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200 p-5 h-fit sticky top-24">
          <h3 className="font-bold text-[#1E3A2B] mb-4">অর্ডার সারাংশ</h3>
          <div className="space-y-2 max-h-48 overflow-auto no-scrollbar mb-3">
            {items.map((i) => (
              <div key={i.product.id} className="flex justify-between text-sm">
                <span className="text-stone-600 truncate mr-2">{i.product.name_bn} × {i.quantity}</span>
                <span className="font-medium shrink-0">{taka(effectivePrice(i.product, i.quantity) * i.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mb-3">
            <input placeholder="কুপন কোড" value={coupon} onChange={(e) => setCoupon(e.target.value)} className="flex-1 h-10 px-3 rounded-lg border border-stone-300 text-sm" data-testid="coupon-input" />
            <button type="button" onClick={applyCoupon} className="h-10 px-4 rounded-lg bg-stone-800 text-white text-sm flex items-center gap-1" data-testid="apply-coupon"><Tag size={14} /> প্রয়োগ</button>
          </div>
          <div className="space-y-2 text-sm border-t border-stone-200 pt-3">
            <div className="flex justify-between"><span className="text-stone-600">সাবটোটাল</span><span>{taka(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-stone-600">ডেলিভারি</span><span>{taka(delivery)}</span></div>
            {discount > 0 && <div className="flex justify-between text-emerald-700"><span>ছাড়</span><span>-{taka(discount)}</span></div>}
            <div className="border-t border-stone-200 pt-2 flex justify-between text-base font-bold text-[#1E3A2B]"><span>সর্বমোট</span><span>{taka(total)}</span></div>
          </div>
          <button type="submit" disabled={submitting} data-testid="place-order-btn" className="mt-5 w-full bg-[#1E3A2B] hover:bg-[#14281D] disabled:opacity-50 text-white font-medium h-12 rounded-xl transition-colors">
            {submitting ? "প্রসেসিং..." : "অর্ডার নিশ্চিত করুন"}
          </button>
        </div>
      </form>
    </div>
  );
}
